import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import Popper from '@mui/material/Popper'

import { theme } from '../../../themes/theme'
import { useRequestSearch, SEARCH_STATUS } from '../../../hooks/redaction/useRequestSearch'
import { MUTED_TEXT_MID, PANEL_BORDER, POPOVER_SHADOW } from '../../styles/colors'
import RequestSearchBar, { SEARCH_HELPER_TEXT } from './RequestSearchBar'
import RequestSearchResults from './RequestSearchResults'

export const SEARCH_PANEL_ID = 'request-search-panel'
const POPOVER_Z_INDEX = 1200
const HINT_MAX_WIDTH = 520

const Anchor = styled.div`
  display: flex;
  align-items: center;
  flex: 1 1 auto;
  min-width: 0;
`

const Surface = styled.div`
  border-radius: 10px;
  background: ${theme.colors.background.white};
  box-shadow: ${POPOVER_SHADOW};
`

const Hint = styled.div`
  padding: 12px 18px;
  border: 1px solid ${PANEL_BORDER};
  border-radius: 10px;
  font-size: 13px;
  line-height: 18px;
  color: ${props => (props.invalid ? theme.colors.error : MUTED_TEXT_MID)};
`

const POPPER_MODIFIERS = [
  { name: 'offset', options: { offset: [0, 10] } },
  { name: 'preventOverflow', options: { padding: 16 } },
  { name: 'flip', enabled: false }
]

// Request-wide search with results displayed in an anchored popover.
function RequestSearch({ foiId, onOpenResult, pollIntervalMs, anchorRef = null }) {
  const search = useRequestSearch(foiId, pollIntervalMs ? { pollIntervalMs } : undefined)
  const [draft, setDraft] = useState(search.query)
  const [expanded, setExpanded] = useState(false)
  const [fieldEl, setFieldEl] = useState(null)
  const rootRef = useRef(null)
  const anchorEl = anchorRef?.current ?? fieldEl
  const [surfaceWidth, setSurfaceWidth] = useState(null)

  // Restore focus only when the keyboard or close button collapses the search.
  const collapse = useCallback((restoreFocus = false) => {
    setExpanded(false)
    if (restoreFocus) rootRef.current?.querySelector('[data-testid="request-search-toggle"]')?.focus()
  }, [])

  // Sync a restored query until the user edits the field.
  const userTouchedRef = useRef(false)
  useEffect(() => {
    if (userTouchedRef.current) return
    if (!search.query && draft) setDraft('')
    else if (search.query && !draft) setDraft(search.query)
  }, [search.query, draft])

  useEffect(() => {
    if (!expanded || !anchorEl) return undefined
    const measure = () => setSurfaceWidth(anchorEl.getBoundingClientRect().width || null)
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [expanded, anchorEl])

  useEffect(() => {
    if (!expanded) return undefined
    function onPointerDown(event) {
      if (rootRef.current?.contains(event.target)) return
      if (event.target.closest?.(`#${SEARCH_PANEL_ID}`)) return
      collapse()
    }
    function onKeyDown(event) {
      if (event.key === 'Escape') collapse(true)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [expanded, collapse])

  function handleChange(value) {
    userTouchedRef.current = true
    setDraft(value)
    search.clearValidation()
  }

  function handleClear() {
    userTouchedRef.current = true
    setDraft('')
    search.clear()
  }

  function handleSelectResult(result) {
    collapse(false)
    onOpenResult(result)
  }

  const invalid = Boolean(search.validationMessage)
  const showResults = search.status !== SEARCH_STATUS.idle && !invalid
  const open = expanded && Boolean(anchorEl)

  return (
    <Anchor ref={rootRef}>
      <div ref={setFieldEl} style={{ display: 'flex', flex: '1 1 auto', minWidth: 0 }}>
        <RequestSearchBar
          expanded={expanded}
          onToggle={() => setExpanded(prev => !prev)}
          onCollapse={() => collapse(true)}
          value={draft}
          onChange={handleChange}
          onSubmit={search.search}
          onClear={handleClear}
          searching={search.status === SEARCH_STATUS.searching}
          validationMessage={search.validationMessage}
          panelId={SEARCH_PANEL_ID}
        />
      </div>
      <Popper
        id={SEARCH_PANEL_ID}
        open={open}
        anchorEl={anchorEl}
        placement="bottom-end"
        modifiers={POPPER_MODIFIERS}
        style={{ zIndex: POPOVER_Z_INDEX }}>
        <Surface
          data-testid="request-search-popover"
          style={{ width: showResults ? (surfaceWidth ?? undefined) : Math.min(surfaceWidth ?? HINT_MAX_WIDTH, HINT_MAX_WIDTH) }}>
          {showResults ? (
            <RequestSearchResults
              status={search.status}
              query={search.query}
              results={search.results}
              totals={search.totals}
              indexing={search.indexing}
              errorMessage={search.errorMessage}
              onClose={() => collapse(true)}
              onRetry={search.retry}
              onSelectResult={handleSelectResult}
            />
          ) : (
            <Hint invalid={invalid} role={invalid ? 'alert' : undefined} aria-hidden={invalid ? undefined : 'true'}>
              {search.validationMessage || SEARCH_HELPER_TEXT}
            </Hint>
          )}
        </Surface>
      </Popper>
    </Anchor>
  )
}

RequestSearch.propTypes = {
  foiId: PropTypes.string.isRequired,
  onOpenResult: PropTypes.func.isRequired,
  pollIntervalMs: PropTypes.number,
  anchorRef: PropTypes.object
}

export default RequestSearch
